type ReturnValue = [string, string | undefined];
type Options = {
  values: string[];
  labels: (string | undefined)[];
};

/**
 * Returns list of unique column values, applying conditional filtering
 * @param table - data structure to filter as a list of rows
 * @param columns - list of columns to retrieve. index 0: primary column. Use this to retrieve a list of value:name pairs
 * @param filters - filters to apply on data table. Will not filter by primary column (columm[0])
 */
export function getConditionalOptions<T extends object>(
  table: T[],
  columns: [keyof T, (keyof T)?],
  filters: Partial<T>
) {
  // retrieve requested columns of filtered data rows
  const filteredTable = filterConditional(table, columns[0], filters).map(
    (row) => columns.map((c) => c && row[c]) as ReturnValue
  );
  // return a unique list of selected values for the requested property
  const uniqueMap = new Map(filteredTable.map((item) => [item[0], item]));
  const options: Options = {
    values: [],
    labels: [],
  };
  for (const o of uniqueMap.values()) {
    options.values.push(o[0]);
    options.labels.push(o[1]);
  }
  return options;
}

/**
 * Returns conditionally filtered rows for a specific column
 */
function filterConditional<T extends object>(
  table: T[],
  column: keyof T,
  filters: Partial<T>
) {
  // filters do not filter itself and must contain a value
  const listOfFilter = (Object.keys(filters) as (keyof T)[]).filter(
    (f) => f !== column && filters[f] != null && filters[f] !== ''
  );
  // Remove each table row if any of the filters does not match its property
  return table.filter(
    (row) => !listOfFilter.some((p) => filters[p] !== row[p])
  );
}
